import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { addDays, dayLabel, requestDate, requestWindowLabel } from '@kronos/domain'
import type { LocalDate } from '@kronos/domain'
import { repository } from '../../data'
import type { ComeInRequestView } from '../../data/schedule'
import { problemOf } from '../hours/time'
import { Modal } from '../kit'
import { relativeDay, useScheduleChanged } from './shared'

/**
 * A request waiting for the technician: when, what for, who asked — and two
 * big buttons. Accept is one tap. Decline asks for a reason, which may be left
 * empty: "can't, sorry" is a complete answer.
 */
export function AnswerRow({
  request,
  timeZone,
  today,
}: {
  request: ComeInRequestView
  timeZone: string
  today: LocalDate
}): JSX.Element {
  const { answer, declining, setDeclining, problem } = useAnswer(request.id)
  const date = requestDate(request, timeZone)
  const when = relativeDay(date, today, addDays(today, 1))

  return (
    <div className="answer-row">
      <div className="row-item__main">
        <span className="row-item__title">
          {when ? `${when} · ` : ''}
          {dayLabel(date)} · <span className="figure">{requestWindowLabel(request, timeZone)}</span>
        </span>
        <span className="row-item__meta">
          {request.note ? `${request.note} — ` : ''}asked by {request.requestedByName}
        </span>
        {problem ? (
          <span className="critical" role="alert">
            {problem}
          </span>
        ) : null}
      </div>
      <div className="answer-row__actions">
        <button className="primary" disabled={answer.isPending} onClick={() => answer.mutate({ answer: 'accept' })}>
          Accept
        </button>
        <button disabled={answer.isPending} onClick={() => setDeclining(true)}>
          Decline
        </button>
      </div>
      {declining ? (
        <DeclineDialog
          busy={answer.isPending}
          title={`${dayLabel(date)}, ${requestWindowLabel(request, timeZone)}`}
          onClose={() => setDeclining(false)}
          onDecline={(reply) => answer.mutate({ answer: 'decline', reply })}
        />
      ) : null}
    </div>
  )
}

/**
 * Answering one request: the mutation, whether the reason dialog is open, and
 * the server's sentence if it refused. Shared by Accept/Decline here and by
 * "Can't make it" on a booked shift.
 */
export function useAnswer(id: string) {
  const changed = useScheduleChanged()
  const [declining, setDeclining] = useState(false)
  const [problem, setProblem] = useState('')
  const answer = useMutation({
    mutationFn: (v: { answer: 'accept' | 'decline'; reply?: string }) => repository.answerRequest(id, v.answer, v.reply),
    onSuccess: () => {
      setDeclining(false)
      setProblem('')
      changed()
    },
    onError: (e) => setProblem(problemOf(e)),
  })
  return { answer, declining, setDeclining, problem }
}

export function DeclineDialog({
  title,
  heading = 'Decline this request?',
  confirm = 'Decline',
  busy,
  onClose,
  onDecline,
}: {
  title: string
  heading?: string
  confirm?: string
  busy: boolean
  onClose: () => void
  onDecline: (reply: string) => void
}): JSX.Element {
  const [reply, setReply] = useState('')
  return (
    <Modal
      title={heading}
      subtitle={title}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Back</button>
          <button className="primary" disabled={busy} onClick={() => onDecline(reply)}>
            {confirm}
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">Anything to add? (optional)</span>
        <input
          data-autofocus
          maxLength={140}
          placeholder="e.g. Away that week, could do Thursday"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
        />
      </label>
    </Modal>
  )
}
